import type { MinimalUser } from "@/components/travel/types";
import { useAuth } from "@clerk/clerk-react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import {
  buildScrapeAccessTokenEndpoint,
  isScrapePath,
} from "@/lib/scrapeAuth";
import {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  type ReactNode,
} from "react";

// Maximum number of token fetch retry attempts
const MAX_TOKEN_RETRY_ATTEMPTS = 3;
// Minimum time (ms) between retry attempts
const MIN_RETRY_INTERVAL = 5000;

/**
 * The token endpoint has shipped two response shapes: the API contract returns
 * `{ token }`, but production token routes (and some proxies) surface the raw
 * Access provider field `{ session_token }`. Reading only `token` silently broke
 * the scrape sign-in flow, so accept either field. Returns a trimmed non-empty
 * token, or null when neither recognized field carries a usable value.
 */
export interface AccessTokenApiResponse {
  token?: string | null;
  session_token?: string | null;
}

export const extractAccessToken = (
  response: AccessTokenApiResponse | null | undefined
): string | null => {
  if (!response) return null;
  for (const candidate of [response.token, response.session_token]) {
    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (trimmed) return trimmed;
    }
  }
  return null;
};

export interface AccessTokenContextType {
  accessToken: string | null;
  isLoading: boolean;
  error: string | null;
  fetchToken: (user: MinimalUser) => Promise<string | null>;
  resetTokenState: () => void;
}

const AccessTokenContext = createContext<AccessTokenContextType>({
  accessToken: null,
  isLoading: false,
  error: null,
  fetchToken: async () => null,
  resetTokenState: () => {},
});

export const useAccessToken = () => useContext(AccessTokenContext);

export const AccessTokenProvider = ({ children }: { children: ReactNode }) => {
  const { getToken } = useAuth();
  const location = useLocation();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const failedAttempts = useRef<number>(0);
  const lastFailedTime = useRef<number>(0);

  const resetTokenState = useCallback(() => {
    setAccessToken(null);
    setError(null);
    failedAttempts.current = 0;
    lastFailedTime.current = 0;
  }, []);

  const fetchToken = useCallback(
    async (user: MinimalUser): Promise<string | null> => {
      // In development, bypass the server and return a local dummy token
      if (import.meta.env.DEV) {
        const devToken = "dev-dummy-access-token-for-testing-purposes-only";
        setAccessToken(devToken);
        setError(null);
        return devToken;
      }

      const scrapeMode = isScrapePath(location.pathname);
      if (!scrapeMode && !user?.id) {
        setError("No user provided for token request");
        return null;
      }

      // Check if we've exceeded max retry attempts
      if (failedAttempts.current >= MAX_TOKEN_RETRY_ATTEMPTS) {
        const errorMsg = `Maximum token fetch attempts (${MAX_TOKEN_RETRY_ATTEMPTS}) reached`;
        setError(errorMsg);
        console.warn(errorMsg);
        return null;
      }

      // Check if we're retrying too quickly after a failure
      const now = Date.now();
      const timeSinceLastFailure = now - lastFailedTime.current;
      if (
        failedAttempts.current > 0 &&
        timeSinceLastFailure < MIN_RETRY_INTERVAL
      ) {
        const errorMsg = `Token fetch attempt too soon after failure (${timeSinceLastFailure}ms)`;
        console.warn(errorMsg);
        setError(errorMsg);
        return null;
      }

      try {
        setIsLoading(true);
        setError(null);

        const clerkToken = scrapeMode ? null : await getToken();
        if (!scrapeMode && !clerkToken) {
          console.warn(
            "[Access Dev] Clerk token unavailable — user may not be fully authenticated."
          );
        }

        const endpoint = buildScrapeAccessTokenEndpoint(
          location.pathname,
          location.search
        );

        if (import.meta.env.DEV) {
          console.info("[Access Dev] Fetching token from:", endpoint);
        }
        const data = await api.get<AccessTokenApiResponse>(
          endpoint,
          clerkToken
        );

        const token = extractAccessToken(data);
        if (!token) {
          // No ambiguous empty-token handling: a response missing both the
          // `token` and `session_token` fields is a hard failure, surfaced to
          // the caller (which renders the public "refresh the page" copy)
          // instead of forwarding an empty string to the SDK.
          console.warn(
            "[Access Dev] Token response missing both `token` and `session_token`. " +
              "Check the tenant-specific ACCESS_DEV_API_KEY__<TENANT_ID> env var on the backend."
          );
          setAccessToken(null);
          setError("Access token response did not contain a usable token");
          return null;
        }

        if (import.meta.env.DEV) {
          console.info("[Access Dev] Token received, length:", token.length);
        }

        // Reset failure tracking on success
        failedAttempts.current = 0;
        lastFailedTime.current = 0;

        // Access session tokens are single-use for SDK startup, so we keep the
        // latest value for debugging/UI state but always fetch a fresh token for
        // each initialization attempt instead of reusing this cached value.
        setAccessToken(token);
        return token;
      } catch (err) {
        failedAttempts.current += 1;
        lastFailedTime.current = Date.now();

        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch access token";
        setError(
          `${errorMessage} (attempt ${failedAttempts.current}/${MAX_TOKEN_RETRY_ATTEMPTS})`
        );
        console.error("Error fetching access token:", errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [getToken, location.pathname, location.search]
  );

  const contextValue: AccessTokenContextType = {
    accessToken,
    isLoading,
    error,
    fetchToken,
    resetTokenState,
  };

  return (
    <AccessTokenContext.Provider value={contextValue}>
      {children}
    </AccessTokenContext.Provider>
  );
};
